import { Controller } from "@hotwired/stimulus"
import { post, patch } from "@rails/request.js"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["cellContainer"]
  static values = { journeyMapId: String }

  initialize() {
    this.cellSortables = new Map()
  }

  disconnect() {
    this.cellSortables.forEach(sortable => sortable.destroy())
    this.cellSortables.clear()
  }

  // --- Card drag-and-drop within and between cells ---
  cellContainerTargetConnected(element) {
    const sortable = Sortable.create(element, {
      group: "journey-map-cards",
      animation: 150,
      ghostClass: "opacity-50",
      chosenClass: "shadow-lg",
      dragClass: "cursor-grabbing",
      draggable: ".journey-map-card",
      onEnd: this.handleCardSortEnd.bind(this)
    })
    this.cellSortables.set(element, sortable)
  }

  cellContainerTargetDisconnected(element) {
    const sortable = this.cellSortables.get(element)
    if (sortable) {
      sortable.destroy()
      this.cellSortables.delete(element)
    }
  }

  // --- Add Card (inline textarea for text/emotion rows) ---
  addCard(event) {
    const button = event.currentTarget
    const stageId = button.dataset.stageId
    const rowId = button.dataset.rowId
    const rowType = button.dataset.rowType

    const cardsContainer = button.closest(".journey-map-cell").querySelector('[data-journey-map-sortable-target="cellContainer"]')

    this.addTextCard(cardsContainer, stageId, rowId, rowType)
  }

  addTextCard(cardsContainer, stageId, rowId, rowType) {
    const tempCard = document.createElement("div")
    tempCard.className = "journey-map-card bg-neutral-primary border border-brand/50 rounded-md p-2"
    tempCard.innerHTML = `
      ${rowType === 'emotion' ? `
        <div class="flex gap-1 mb-1 emoji-picker">
          <button type="button" class="emoji-btn text-sm p-0.5 rounded hover:bg-neutral-secondary" data-emoji="😍" title="Delighted">😍</button>
          <button type="button" class="emoji-btn text-sm p-0.5 rounded hover:bg-neutral-secondary" data-emoji="😊" title="Happy">😊</button>
          <button type="button" class="emoji-btn text-sm p-0.5 rounded hover:bg-neutral-secondary" data-emoji="😐" title="Neutral">😐</button>
          <button type="button" class="emoji-btn text-sm p-0.5 rounded hover:bg-neutral-secondary" data-emoji="😤" title="Frustrated">😤</button>
          <button type="button" class="emoji-btn text-sm p-0.5 rounded hover:bg-neutral-secondary" data-emoji="😠" title="Angry">😠</button>
        </div>
      ` : ''}
      <textarea
        class="w-full text-xs p-0 border-0 resize-none focus:ring-0 focus:outline-none bg-transparent text-heading placeholder-body-subtle"
        placeholder="${rowType === 'emotion' ? 'How does the customer feel?' : 'Enter card content...'}"
        rows="2"
        data-stage-id="${stageId}"
        data-row-id="${rowId}"
        data-emoji=""
      ></textarea>
      <input type="hidden" class="selected-emoji" value="">
    `

    cardsContainer.appendChild(tempCard)

    const textarea = tempCard.querySelector("textarea")
    textarea.focus()

    textarea.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        this.saveNewCard(textarea, tempCard)
      }
      if (e.key === "Escape") {
        tempCard.remove()
      }
    })
    textarea.addEventListener("blur", (e) => {
      setTimeout(() => {
        if (document.activeElement !== textarea && tempCard.isConnected) {
          this.saveNewCard(textarea, tempCard)
        }
      }, 200)
    })

    const emojiButtons = tempCard.querySelectorAll(".emoji-btn")
    emojiButtons.forEach(btn => {
      btn.addEventListener("click", (e) => {
        e.preventDefault()
        e.stopPropagation()
        emojiButtons.forEach(b => b.classList.remove("bg-brand/20", "ring-1", "ring-brand"))
        btn.classList.add("bg-brand/20", "ring-1", "ring-brand")
        tempCard.querySelector(".selected-emoji").value = btn.dataset.emoji
        textarea.focus()
      })
    })
  }

  async saveNewCard(textarea, tempCard) {
    if (tempCard.dataset.saving === "true") return

    const content = textarea.value.trim()
    const stageId = textarea.dataset.stageId
    const rowId = textarea.dataset.rowId
    const emoji = tempCard.querySelector(".selected-emoji")?.value || ""

    if (!content) {
      tempCard.remove()
      return
    }

    tempCard.dataset.saving = "true"

    try {
      const response = await post(`/workspace/journey_maps/${this.journeyMapIdValue}/journey_map_cards`, {
        body: JSON.stringify({
          journey_map_card: {
            journey_map_stage_id: stageId,
            journey_map_row_id: rowId,
            content: content,
            emoji: emoji
          }
        }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        tempCard.remove()
      } else {
        const data = await response.json
        console.error("Failed to create card:", data.error)
        tempCard.dataset.saving = "false"
      }
    } catch (error) {
      console.error("Error creating card:", error)
      tempCard.dataset.saving = "false"
    }
  }

  // --- Insert stage/row at position ---
  async insertStage(event) {
    const button = event.currentTarget
    const journeyMapId = this.journeyMapIdValue
    const position = button.dataset.position
    const stageCount = document.querySelectorAll("[data-stage-id]").length

    try {
      const response = await post(`/workspace/journey_maps/${journeyMapId}/journey_map_stages`, {
        body: JSON.stringify({
          journey_map_stage: {
            name: `Stage ${stageCount + 1}`,
            position: parseInt(position)
          }
        }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        window.Turbo.visit(window.location.href)
      }
    } catch (error) {
      console.error("Error inserting stage:", error)
    }
  }

  async insertRow(event) {
    const button = event.currentTarget
    const journeyMapId = this.journeyMapIdValue
    const position = button.dataset.position
    const rowCount = document.querySelectorAll(".journey-map-row").length

    try {
      const response = await post(`/workspace/journey_maps/${journeyMapId}/journey_map_rows`, {
        body: JSON.stringify({
          journey_map_row: {
            name: `Row ${rowCount + 1}`,
            position: parseInt(position)
          }
        }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        window.Turbo.visit(window.location.href)
      }
    } catch (error) {
      console.error("Error inserting row:", error)
    }
  }

  // --- Drag & Drop handler ---

  async handleCardSortEnd(event) {
    const cardElement = event.item
    const cardId = cardElement.dataset.cardId
    const targetCell = event.to
    const newStageId = targetCell.dataset.stageId
    const newRowId = targetCell.dataset.rowId
    const newPosition = event.newIndex

    if (event.from === event.to && event.oldIndex === event.newIndex) return

    try {
      await patch(`/workspace/journey_map_cards/${cardId}/reorder`, {
        body: JSON.stringify({
          journey_map_stage_id: newStageId,
          journey_map_row_id: newRowId,
          position: newPosition
        }),
        contentType: "application/json",
        responseKind: "json"
      })
    } catch (error) {
      console.error("Error reordering card:", error)
    }
  }
}
